// Calendar (DESIGN §3): "When is it happening?" — a dark Google-Calendar-style Week (default) / Day / Month view over
// derive.calendarItems(). Route: #/calendar?view=month|week|day&date=YYYY-MM-DD (view + date live in the query;
// phones default to Day, and their Week is an agenda list).
// Interactions: click (or drag across) empty time → quick-create popover; drag a block to move it (15-min snap, across
// days; touch = press-and-hold); drag its bottom edge to resize; click → event popover (notes, calendar, Open Batch ↗,
// Delete). Moves/resizes persist with dispatch('event.update', { id, patch: { start, end } }).
// Derived "Batch due" and "Posting · <Client>" items (DESIGN §9) are all-day chips, never draggable; a posting chip
// opens its client.
import './calendar.css';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { dispatch, getSnapshot, reload, useStore } from '../store.js';
import { isNotesConflict } from '../components/notes-sync.js';
import { mergeNotes } from '../components/notes-merge.js';
import { batchHref, clientHref, href, navigate, setQuery } from '../router.js';
import { Icon } from '../icons.jsx';
import { Page } from '../components/page.jsx';
import { Button, ConfirmButton, IconButton, StatusBadge, cx, toast } from '../components/ui.jsx';
import { FilterButtons } from '../components/filter-buttons.jsx';
import { CompactDropdown } from '../components/compact-dropdown.jsx';
import { Popover, usePopover } from '../components/popover.jsx';
import { StageProgress } from '../components/stage-progress.jsx';
import { openEvent } from '../components/forms.jsx';
import {
  CALENDAR_NAMES, CALENDAR_TONES, CalendarEvent, compactRange, compactTime, isBatchDue, isDerived, isOverdueItem, isPosting, shortTitle,
  whenText,
} from '../components/calendar-event.jsx';
import { CALENDAR_TYPES, batchLabel, monthLabel, stageByKey } from '../../shared/constants.js';
import { calendarItems, isOffBatch, teamName } from '../../shared/derive.js';
import {
  addDays, addMonths, datePart, diffDays, endOfMonth, endOfWeek, formatDate, formatMonthYear, isISODate,
  startOfMonth, startOfWeek, timePart,
} from '../../shared/dates.js';

// ---------- constants ----------

export const CALENDAR_VIEWS = ['month', 'week', 'day'];
const VIEW_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];
const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const HOUR_PX = 48; // time grid: pixels per hour (15 min = 12px)
const SNAP = 15; // minutes
const DAY_MIN = 24 * 60;
const MIN_BLOCK = 25; // shortest block drawn (minutes) — keeps one line of text readable
const MIN_COL_PX = 60; // narrowest readable side-by-side column; more overlaps collapse into "+N"
const MORE_GUTTER = 22; // px reserved for the "+N" marker of an overflowing cluster
const ALLDAY_LANES = 2; // all-day lanes shown before "+N"
const MONTH_CHIPS = 3; // chips per month cell before "+N more"
const DEFAULT_SCROLL_H = 7.5; // first visible hour when nothing earlier is scheduled

const FILTERS_KEY = 'frazier-calendar-filters'; // per-viewer convenience (localStorage, best effort)
const PHONE_QUERY = '(max-width: 767.98px)';
const COMPACT_QUERY = '(max-width: 899.98px)'; // Week → agenda, Month → dot grid (7 columns are unreadable below this)

// ---------- pure helpers (exported for reuse) ----------

// '#/calendar?view=day&date=2026-09-25'
export function calendarHref(view, date) {
  return href(`/calendar?view=${view}&date=${date}`);
}

// The period a view represents: month → the calendar month, week → Mon–Sun, day → that day.
export function periodRange(view, date) {
  if (view === 'day') return { from: date, to: date };
  if (view === 'week') return { from: startOfWeek(date), to: endOfWeek(date) };
  return { from: startOfMonth(date), to: endOfMonth(date) };
}

// What a view draws: the month view shows a Monday-first 6×7 grid around its month.
export function visibleRange(view, date) {
  if (view !== 'month') return periodRange(view, date);
  const from = startOfWeek(startOfMonth(date));
  return { from, to: addDays(from, 41) };
}

// Month steps land on the 1st (or today when today is in that month) so short months never drift the anchor day.
export function shiftDate(view, date, dir, today = '') {
  if (view === 'month') {
    const first = addMonths(startOfMonth(date), dir);
    return today && today.slice(0, 7) === first.slice(0, 7) ? today : first;
  }
  return addDays(date, view === 'week' ? 7 * dir : dir);
}

// 'September 2026' · 'Sep – Oct 2026' · 'Dec 2026 – Jan 2027' · 'Fri, Sep 25, 2026'
export function periodTitle(view, date) {
  if (view === 'day') return `${formatDate(date, 'weekday')}, ${date.slice(0, 4)}`;
  if (view === 'month') return formatMonthYear(date);
  const s = startOfWeek(date);
  const e = endOfWeek(date);
  if (s.slice(0, 7) === e.slice(0, 7)) return formatMonthYear(s);
  const mon = (d) => formatDate(d).slice(0, 3);
  if (s.slice(0, 4) === e.slice(0, 4)) return `${mon(s)} – ${mon(e)} ${e.slice(0, 4)}`;
  return `${mon(s)} ${s.slice(0, 4)} – ${mon(e)} ${e.slice(0, 4)}`;
}

export const toMinutes = (hhmm) => {
  const m = /^(\d{2}):(\d{2})/.exec(hhmm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};
// 570 → '09:30'; the end of the day (1440) is stored as 23:59 (timed events end on the day they start).
export function hhmm(min) {
  const m = Math.max(0, Math.min(Math.round(min), DAY_MIN - 1));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
const snap = (min, step = SNAP) => Math.round(min / step) * step;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const weekdayIndex = (d) => (diffDays('2024-01-01', d) % 7 + 7) % 7; // 2024-01-01 was a Monday → 0
const dayNumber = (d) => Number(d.slice(8));

// First and last day an item covers (a timed item ending exactly at midnight does not cover the next day).
export function itemDays(item) {
  const s = datePart(item.start);
  let e = item.end ? datePart(item.end) : s;
  if (!item.allDay && item.end && e > s && timePart(item.end) === '00:00') e = addDays(e, -1);
  return [s, e < s ? s : e];
}

// Display order inside a day: open batch deadlines first, then all-day events, timed events by time, the derived
// Posting chips (every posting day of every client — they must not crowd real events out of a month cell), done
// batches last.
function rank(item) {
  if (isBatchDue(item)) return item.status === 'Done' ? 3 : 0;
  if (isPosting(item)) return 2.5;
  return item.allDay ? 1 : 2;
}
export function displaySort(a, b) {
  return rank(a) - rank(b) || (a.allDay || b.allDay ? 0 : a.start.localeCompare(b.start)) || a.title.localeCompare(b.title);
}

// Map<'YYYY-MM-DD', item[]> for [from, to]; multi-day items appear on every day they cover.
export function groupByDay(items, from, to) {
  const map = new Map();
  for (const item of items) {
    let [s, e] = itemDays(item);
    if (s < from) s = from;
    if (e > to) e = to;
    for (let d = s; d <= e; d = addDays(d, 1)) {
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(item);
    }
  }
  for (const list of map.values()) list.sort(displaySort);
  return map;
}

// The part of a timed item drawn on `day`, in minutes: { s, e } with 0 ≤ s < e ≤ 1440 (end capped at 24:00 — LOGIC-C2).
export function segmentFor(item, day) {
  const sd = datePart(item.start);
  const ed = item.end ? datePart(item.end) : sd;
  const s = sd < day ? 0 : toMinutes(timePart(item.start));
  let e = !item.end ? s + 60 : ed > day ? DAY_MIN : toMinutes(timePart(item.end));
  if (e <= s) e = s + SNAP;
  return { s: clamp(s, 0, DAY_MIN - SNAP), e: clamp(e, SNAP, DAY_MIN) };
}

// Side-by-side columns for overlapping timed segments ({ s, e } minutes). Each cluster of transitively overlapping
// segments shares a column count, capped at `maxCols`; a segment widens into free columns to its right. When a
// cluster needs more columns, it keeps `overflowCols` readable columns and the rest become one "+N" marker:
// → { placed: [seg + { col, cols, span, gutter }], more: [{ s, count, items }] }
export function layoutTimed(segments, maxCols = 99, overflowCols = maxCols) {
  const sorted = [...segments].sort((a, b) => a.s - b.s || b.e - a.e);
  const clusters = [];
  let cluster = null;
  for (const seg of sorted) {
    seg.vEnd = Math.max(seg.e, seg.s + MIN_BLOCK);
    if (!cluster || seg.s >= cluster.end) {
      cluster = { items: [], end: 0, colEnds: [] };
      clusters.push(cluster);
    }
    let col = cluster.colEnds.findIndex((end) => end <= seg.s);
    if (col < 0) {
      col = cluster.colEnds.length;
      cluster.colEnds.push(seg.vEnd);
    } else {
      cluster.colEnds[col] = seg.vEnd;
    }
    seg.col = col;
    cluster.items.push(seg);
    cluster.end = Math.max(cluster.end, seg.vEnd);
  }
  const placed = [];
  const more = [];
  for (const c of clusters) {
    const need = c.colEnds.length;
    const overflow = need > maxCols;
    const n = overflow ? Math.max(1, Math.min(overflowCols, maxCols)) : need;
    const shown = c.items.filter((seg) => seg.col < n);
    const hidden = c.items.filter((seg) => seg.col >= n);
    for (const seg of shown) {
      let span = 1;
      while (seg.col + span < n && !shown.some((o) => o.col === seg.col + span && o.s < seg.vEnd && o.vEnd > seg.s)) span++;
      placed.push(Object.assign(seg, { cols: n, span, gutter: overflow }));
    }
    if (hidden.length) {
      more.push({ s: Math.min(...hidden.map((h) => h.s)), count: hidden.length, items: c.items.map((x) => x.item) });
    }
  }
  return { placed, more };
}

// How many side-by-side columns fit in a day column `width` px wide (normal, and when a "+N" gutter is needed).
export function columnCaps(width) {
  if (!width) return { maxCols: 3, overflowCols: 2 };
  return {
    maxCols: Math.max(1, Math.floor((width - 4) / MIN_COL_PX)),
    overflowCols: Math.max(1, Math.floor((width - 4 - MORE_GUTTER) / MIN_COL_PX)),
  };
}

// Lanes for all-day items across consecutive days (multi-day items span columns).
export function allDayLanes(days, items) {
  const first = days[0];
  const last = days[days.length - 1];
  const col = new Map(days.map((d, i) => [d, i]));
  const entries = items.map((item) => {
    const [s, e] = itemDays(item);
    return { item, a: s < first ? 0 : col.get(s), b: e > last ? days.length - 1 : col.get(e) };
  }).filter((en) => en.a !== undefined && en.b !== undefined);
  entries.sort((x, y) => x.a - y.a || (y.b - y.a) - (x.b - x.a) || displaySort(x.item, y.item));
  const ends = [];
  for (const en of entries) {
    let lane = ends.findIndex((end) => end < en.a);
    if (lane < 0) {
      lane = ends.length;
      ends.push(en.b);
    } else {
      ends[lane] = en.b;
    }
    en.lane = lane;
  }
  return { entries, laneCount: ends.length };
}

// ---------- small hooks ----------

function useMedia(query) {
  const read = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

function readFilters() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTERS_KEY) || 'null');
    if (saved && typeof saved === 'object') return { main: saved.main !== false, internal: saved.internal !== false, posting: saved.posting !== false };
  } catch {
    // storage blocked or corrupt: every calendar
  }
  return { main: true, internal: true, posting: true };
}

function writeFilters(filters) {
  try {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch {
    // best effort only
  }
}

function useNowMinutes() {
  const read = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };
  const [minutes, setMinutes] = useState(read);
  useEffect(() => {
    const timer = setInterval(() => setMinutes(read()), 60000);
    return () => clearInterval(timer);
  }, []);
  return minutes;
}

// Width of an element, kept current with a ResizeObserver.
function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return width;
}

// Is a keyboard event aimed at something that takes typing (text inputs, selects, editable areas)?
function isTypingTarget(t) {
  if (!(t instanceof Element)) return false;
  if (t.closest('textarea, select, [contenteditable]:not([contenteditable="false"])')) return true;
  const input = t.closest('input');
  return !!input && !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(input.type);
}

// ---------- view ----------

export function CalendarView({ route }) {
  const { state, index, today } = useStore();
  const query = route?.query || {};
  const isPhone = useMedia(PHONE_QUERY);
  const isCompact = useMedia(COMPACT_QUERY);
  const view = CALENDAR_VIEWS.includes(query.view) ? query.view : isPhone ? 'day' : 'week';
  const date = isISODate(query.date) ? query.date : today;
  const [filters, setFilters] = useState(readFilters);
  const [pop, setPop] = useState(null); // { type: 'event' | 'draft' | 'more', … } — at most one popover
  const draftRef = useRef(null); // the ghost block/chip a quick-create popover points at
  const mainRef = useRef(null);

  const visible = visibleRange(view, date);
  const items = useMemo(
    () => (state && index ? withOff(index, calendarItems(state, index, { from: visible.from, to: visible.to, calendars: filters })) : []),
    [state, index, visible.from, visible.to, filters],
  );
  const byDay = useMemo(() => groupByDay(items, visible.from, visible.to), [items, visible.from, visible.to]);

  const go = (nextView, nextDate) => setQuery({ view: nextView, date: nextDate });
  const step = (dir) => go(view, shiftDate(view, date, dir, today));
  const toggleFilter = (key) => setFilters((f) => {
    const next = { ...f, [key]: !f[key] };
    writeFilters(next);
    return next;
  });
  const defaultCalendar = !filters.main && filters.internal ? 'internal' : 'main';
  const createFull = (defaults = {}) => {
    setPop(null);
    openEvent({ defaults: { calendarType: defaultCalendar, ...defaults } });
  };
  const createAtDate = () => {
    // The selected day at the next full hour (09:00 on other days).
    const h = date === today ? Math.min(22, new Date().getHours() + 1) : 9;
    createFull({ start: `${date}T${hhmm(h * 60)}`, end: `${date}T${hhmm(h * 60 + 60)}` });
  };

  // Keyboard: t today · ←/→ previous/next · m/w/d views · c create. Ignored while typing, inside dialogs/popovers,
  // and (for arrows) while a control has focus — arrows then belong to that control (A11Y-29, QA-06).
  const latest = useRef(null);
  latest.current = { view, date, today, go, step, createAtDate };
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      const t = e.target;
      if (isTypingTarget(t)) return;
      if (document.querySelector('[aria-modal="true"], .popover')) return;
      const s = latest.current;
      const onControl = t instanceof Element && t.closest('button, a, [role="button"], [role="gridcell"], input');
      const actions = {
        t: () => s.go(s.view, s.today),
        m: () => s.go('month', s.date),
        w: () => s.go('week', s.date),
        d: () => s.go('day', s.date),
        c: () => s.createAtDate(),
        ...(onControl ? {} : { ArrowLeft: () => s.step(-1), ArrowRight: () => s.step(1) }),
      };
      const run = actions[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (!run) return;
      e.preventDefault();
      run();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => { setPop(null); }, [view, date, isCompact]);

  // Popover openers shared by every layout.
  const openItem = (item, el, day = datePart(item.start)) => {
    if (isPosting(item)) {
      setPop(null);
      if (item.clientId) navigate(clientHref(item.clientId).slice(1));
      return;
    }
    setPop((cur) => (cur?.type === 'event' && cur.id === item.id && cur.day === day ? null : { type: 'event', id: item.id, day, anchorRef: { current: el } }));
  };
  const openDraft = (draft) => setPop((cur) => ({
    type: 'draft',
    draft: { calendarType: defaultCalendar, title: cur?.type === 'draft' ? cur.draft.title : '', ...draft },
  }));
  const openMore = (list, el, day) => setPop({ type: 'more', items: list, day, anchorRef: { current: el } });
  const closePop = () => setPop(null);
  const focusMain = () => mainRef.current?.focus({ preventScroll: true });

  if (!state || !index) return null;

  const common = {
    today, index, byDay, items, onOpen: openItem, onDraft: openDraft, onMore: openMore, draftRef,
    draft: pop?.type === 'draft' ? pop.draft : null, selectedId: pop?.type === 'event' ? pop.id : '',
  };
  let body;
  if (view === 'month') {
    body = isCompact
      ? <MonthCompact date={date} onPickDay={(d) => go('day', d)} {...common} />
      : <MonthGrid date={date} onPickDay={(d) => go('day', d)} {...common} />;
  } else if (view === 'week' && isCompact) {
    const start = startOfWeek(date);
    body = <Agenda days={Array.from({ length: 7 }, (_, i) => addDays(start, i))} onPickDay={(d) => go('day', d)} {...common} />;
  } else {
    const days = view === 'week' ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(date), i)) : [date];
    body = <TimeGrid key={view} days={days} onPickDay={(d) => go('day', d)} {...common} />;
  }

  return (
    <Page
      wide
      class={cx('cal-page', (view === 'day' || !isCompact) && 'is-fixed')}
      header={(
        <Toolbar
          view={view} date={date} today={today} filters={filters}
          onView={(v) => go(v, date)} onStep={step} onToday={() => go(view, today)} onPick={(d) => go(view, d)}
          onToggle={toggleFilter} onCreate={createAtDate}
        />
      )}
    >
      <div class="cal-layout">
        <aside class="cal-side" aria-label="Calendars">
          <Button variant="primary" icon="plus" class="cal-create" onClick={createAtDate}>Create</Button>
          <MiniMonth value={date} today={today} weekMode={view === 'week'} onSelect={(d) => go(view, d)} />
          <MyCalendars filters={filters} onToggle={toggleFilter} />
        </aside>
        <section class={cx('cal-main', `is-${view}`)} ref={mainRef} tabIndex={-1} aria-label={`${periodTitle(view, date)} ${view}`}>
          {!filters.main && !filters.internal && !filters.posting && (
            <p class="cal-notice" role="status">All calendars are hidden — turn one on under My calendars.</p>
          )}
          {body}
        </section>
      </div>
      {pop?.type === 'event' && <EventPopover target={pop} onClose={closePop} afterDelete={focusMain} />}
      {pop?.type === 'draft' && (
        <QuickCreate
          draft={pop.draft} anchorRef={draftRef} onChange={(patch) => setPop((cur) => (cur?.type === 'draft' ? { ...cur, draft: { ...cur.draft, ...patch } } : cur))}
          onClose={closePop} onMore={(defaults) => createFull(defaults)}
        />
      )}
      {pop?.type === 'more' && <MorePopover target={pop} today={today} index={index} onClose={closePop} onOpen={openItem} />}
    </Page>
  );
}

// ---------- toolbar & sidebar ----------

function Toolbar({ view, date, today, filters, onView, onStep, onToday, onPick, onToggle, onCreate }) {
  const picker = usePopover();
  const cals = usePopover();
  const title = periodTitle(view, date);
  return (
    <header class="cal-toolbar">
      <div class="cal-nav">
        <IconButton icon="chevronLeft" size="sm" label={`Previous ${view} (←)`} onClick={() => onStep(-1)} />
        <Button size="sm" class="cal-today" onClick={onToday} disabled={date === today} title="Today (T)">Today</Button>
        <IconButton icon="chevronRight" size="sm" label={`Next ${view} (→)`} onClick={() => onStep(1)} />
        <h1 class="cal-title">
          <span class="sr-only">Calendar, </span>
          <button type="button" class="cal-title-btn" ref={picker.anchorRef} {...picker.triggerProps} aria-label={`${title} — choose a date`}>
            <span class="truncate">{title}</span>
            <Icon name="chevronDown" size={14} class="cal-title-caret" />
          </button>
        </h1>
        <Popover open={picker.open} onClose={picker.close} anchorRef={picker.anchorRef} placement="bottom-start" width={264} label="Choose a date">
          <div class="cal-pop-pad">
            <MiniMonth value={date} today={today} weekMode={view === 'week'} autoFocus onSelect={(d) => { picker.close(); onPick(d); }} />
          </div>
        </Popover>
      </div>
      <div class="cal-tools">
        <FilterButtons options={VIEW_OPTIONS} value={view} onChange={onView} label="Calendar view" size="sm" variant="text" class="cal-views" />
        <span class="cal-compact-only">
          <button type="button" class="icon-btn icon-btn-sm" aria-label="My calendars" title="My calendars" ref={cals.anchorRef} {...cals.triggerProps}>
            <Icon name="calendar" size={14} />
          </button>
          <Popover open={cals.open} onClose={cals.close} anchorRef={cals.anchorRef} placement="bottom-end" width={220} label="My calendars">
            <div class="cal-pop-pad"><MyCalendars filters={filters} onToggle={onToggle} /></div>
          </Popover>
          <IconButton icon="plus" size="sm" variant="primary" label="Create event (C)" onClick={onCreate} />
        </span>
      </div>
    </header>
  );
}

// MY CALENDARS: the two event calendars + the derived Posting calendar (DESIGN §9).
const MY_CALENDARS = [...CALENDAR_TYPES.filter((c) => c.key !== 'posting'), { key: 'posting', label: 'Posting' }];

function MyCalendars({ filters, onToggle }) {
  return (
    <fieldset class="cal-cals">
      <legend class="cal-side-label">My calendars</legend>
      {MY_CALENDARS.map((c) => (
        <label key={c.key} class={cx('cal-cal', `tone-${CALENDAR_TONES[c.key]}`)}>
          <input type="checkbox" class="cal-cal-input" checked={!!filters[c.key]} onChange={() => onToggle(c.key)} />
          <span class="cal-cal-box" aria-hidden="true"><Icon name="check" size={12} /></span>
          <span class="cal-cal-name">{c.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

// Mini month (sidebar + date picker): Monday-first 6×7 grid, roving tabindex, arrows/PageUp/PageDown/Home/End.
export function MiniMonth({ value, today, onSelect, weekMode = false, autoFocus = false, label = 'Choose a date' }) {
  const [cursor, setCursor] = useState(() => startOfMonth(value));
  const [focusDate, setFocusDate] = useState(value);
  const gridRef = useRef(null);
  const pendingFocus = useRef(autoFocus);

  useEffect(() => {
    setCursor(startOfMonth(value));
    setFocusDate(value);
  }, [value]);

  useLayoutEffect(() => {
    if (!pendingFocus.current) return;
    pendingFocus.current = false;
    gridRef.current?.querySelector(`[data-date="${focusDate}"]`)?.focus();
  }, [focusDate, cursor]);

  const start = startOfWeek(cursor);
  const weeks = Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i)));
  const inGrid = (d) => d >= weeks[0][0] && d <= weeks[5][6];
  const tabbable = inGrid(focusDate) ? focusDate : inGrid(value) ? value : cursor;
  const monthKey = cursor.slice(0, 7);

  const onKeyDown = (e) => {
    const deltas = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const from = e.target?.dataset?.date || tabbable;
    let next = null;
    if (e.key in deltas) next = addDays(from, deltas[e.key]);
    else if (e.key === 'PageUp') next = addMonths(from, -1);
    else if (e.key === 'PageDown') next = addMonths(from, 1);
    else if (e.key === 'Home') next = startOfWeek(from);
    else if (e.key === 'End') next = endOfWeek(from);
    if (!next) return;
    e.preventDefault();
    pendingFocus.current = true;
    setFocusDate(next);
    if (next.slice(0, 7) !== monthKey) setCursor(startOfMonth(next));
  };

  return (
    <div class="cal-mini">
      <div class="cal-mini-head">
        <span class="cal-mini-title" aria-live="polite">{formatMonthYear(cursor)}</span>
        <IconButton icon="chevronLeft" size="sm" label="Previous month" onClick={() => setCursor(addMonths(cursor, -1))} />
        <IconButton icon="chevronRight" size="sm" label="Next month" onClick={() => setCursor(addMonths(cursor, 1))} />
      </div>
      <table ref={gridRef} class="cal-mini-grid" role="grid" aria-label={label} onKeyDown={onKeyDown}>
        <thead>
          <tr>{WEEKDAY_FULL.map((w) => <th key={w} scope="col" abbr={w}><span aria-hidden="true">{w[0]}</span></th>)}</tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]} class={cx(weekMode && week.includes(value) && 'is-week')}>
              {week.map((d) => (
                <td key={d} role="gridcell" aria-selected={d === value ? 'true' : 'false'}>
                  <button
                    type="button"
                    data-date={d}
                    tabIndex={d === tabbable ? 0 : -1}
                    class={cx('cal-mini-day', d.slice(0, 7) !== monthKey && 'is-outside', d === today && 'is-today', d === value && !weekMode && 'is-selected')}
                    aria-label={formatDate(d, 'full')}
                    aria-current={d === today ? 'date' : undefined}
                    onFocus={() => setFocusDate(d)}
                    onClick={() => onSelect(d)}
                  >
                    {dayNumber(d)}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- week / day time grid ----------

const pxOf = (min) => (min * HOUR_PX) / 60;
const hourLabel = (h) => (h === 0 ? '' : h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h - 12} PM`);
// Only single-day timed events can be dragged (derived batch-due items and overnight events cannot).
const canDrag = (item) => !isDerived(item) && !item.allDay && (!item.end || datePart(item.end) === datePart(item.start));

// 570, 660 → '9:30 – 11am' (1440 = midnight).
export function minuteRange(s, e) {
  if (e >= DAY_MIN) return `${compactTime(hhmm(s))} – 12am`;
  return compactRange(`2000-01-01T${hhmm(s)}`, `2000-01-01T${hhmm(e)}`);
}

function findTimed(byDay, days, id) {
  for (const d of days) for (const it of byDay.get(d) || []) if (it.id === id) return it;
  return null;
}

function TimeGrid({ days, today, index, byDay, onOpen, onDraft, onMore, onPickDay, draftRef, draft, selectedId }) {
  const single = days.length === 1;
  const scrollRef = useRef(null);
  const colsRef = useRef(null);
  const width = useWidth(colsRef);
  const caps = columnCaps(width / days.length);
  const nowMin = useNowMinutes();
  const [expanded, setExpanded] = useState(false);
  const [drag, setDrag] = useState(null); // live preview { id, day, s, e, mode }
  const [pending, setPending] = useState(null); // committed move shown until the store has it
  const suppressClick = useRef(false);

  // Start scrolled to the morning (or to the earliest timed item of the period when it is earlier).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let first = DEFAULT_SCROLL_H * 60;
    for (const d of days) for (const it of byDay.get(d) || []) if (!it.allDay) first = Math.min(first, segmentFor(it, d).s - 30);
    el.scrollTop = pxOf(Math.max(0, first));
  }, [days[0], days.length]);

  // Per-day timed segments (with the dragged/pending item moved to its new slot) and all-day items.
  const override = pending;
  const columns = days.map((day) => {
    const list = byDay.get(day) || [];
    const segs = [];
    for (const item of list) {
      if (item.allDay) continue;
      if (override && item.id === override.id) continue;
      segs.push({ item, day, ...segmentFor(item, day) });
    }
    if (override && override.day === day) {
      const item = findTimed(byDay, days, override.id);
      if (item) {
        const moved = { ...item, start: `${day}T${hhmm(override.s)}`, end: item.end ? `${day}T${hhmm(override.e)}` : '' };
        segs.push({ item: moved, day, s: override.s, e: override.e });
      }
    }
    return { day, ...layoutTimed(segs, caps.maxCols, caps.overflowCols) };
  });
  const allDayItems = [];
  const seen = new Set();
  for (const d of days) for (const it of byDay.get(d) || []) if (it.allDay && !seen.has(it.id)) { seen.add(it.id); allDayItems.push(it); }
  const lanes = allDayLanes(days, allDayItems);
  const dragItem = drag && drag.mode !== 'create' ? findTimed(byDay, days, drag.id) : null;
  const clientName = (item) => index.clientsById.get(item.clientId)?.name || '';

  // ----- pointer gestures: move / resize a block, create by click or drag on empty time -----
  // Mouse/pen start after 4px of movement. Touch: a block moves after a 350ms press-and-hold (a quicker swipe scrolls),
  // the resize handle drags at once, and a tap on empty time creates.
  const gesture = useRef(null);
  const slotAt = (x, y) => {
    const r = colsRef.current.getBoundingClientRect();
    const i = clamp(Math.floor((x - r.left) / (r.width / days.length)), 0, days.length - 1);
    return { day: days[i], min: ((y - r.top) * 60) / HOUR_PX };
  };
  const preview = (g, x, y) => {
    const p = slotAt(x, y);
    let next;
    if (g.mode === 'move') {
      const s = clamp(snap(p.min - g.offset), 0, DAY_MIN - Math.max(g.dur, SNAP));
      next = { day: p.day, s, e: s + g.dur };
    } else if (g.mode === 'resize') {
      next = { day: g.seg.day, s: g.seg.s, e: clamp(snap(p.min), g.seg.s + SNAP, DAY_MIN) };
    } else {
      const a = clamp(Math.floor(g.origin.min / SNAP) * SNAP, 0, DAY_MIN - SNAP);
      const b = clamp(snap(p.min), 0, DAY_MIN);
      next = { day: g.origin.day, s: Math.min(a, b), e: Math.max(a + SNAP, b) };
    }
    g.last = next;
    setDrag({ ...next, mode: g.mode, id: g.item?.id || '' });
  };
  const autoScroll = (y) => {
    const el = scrollRef.current;
    const r = el.getBoundingClientRect();
    if (y < r.top + 32) el.scrollTop -= 14;
    else if (y > r.bottom - 32) el.scrollTop += 14;
  };
  const end = () => {
    const g = gesture.current;
    if (!g) return;
    clearTimeout(g.timer);
    window.removeEventListener('pointermove', g.onMove);
    window.removeEventListener('pointerup', g.onUp);
    window.removeEventListener('pointercancel', g.onCancel);
    window.removeEventListener('touchmove', g.onTouchMove);
    window.removeEventListener('keydown', g.onKey, true);
    gesture.current = null;
  };
  useEffect(() => end, []);

  const commit = (g) => {
    const next = g.last;
    if (g.mode === 'create') {
      setDrag(null);
      onDraft({ day: next.day, s: next.s, e: next.e, allDay: false });
      return;
    }
    const { item, seg } = g;
    setDrag(null);
    if (!next || (next.day === seg.day && next.s === seg.s && next.e === seg.e)) return;
    const start = `${next.day}T${hhmm(next.s)}`;
    const endValue = item.end || g.mode === 'resize' ? `${next.day}T${hhmm(next.e)}` : '';
    setPending({ id: item.id, day: next.day, s: next.s, e: next.e });
    dispatch('event.update', { id: item.eventId, patch: { start, end: endValue } })
      .catch(() => {})
      .finally(() => setPending(null));
  };

  const begin = (e, mode, seg = null) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (gesture.current) end();
    const touch = e.pointerType === 'touch';
    const origin = slotAt(e.clientX, e.clientY);
    const g = {
      id: e.pointerId, mode, seg, item: seg?.item, touch, active: false, last: null, timer: 0,
      x0: e.clientX, y0: e.clientY, origin, offset: seg ? origin.min - seg.s : 0, dur: seg ? seg.e - seg.s : 60,
    };
    const activate = () => {
      g.active = true;
      navigator.vibrate?.(8);
      preview(g, g.x, g.y);
    };
    g.x = e.clientX;
    g.y = e.clientY;
    g.onMove = (ev) => {
      if (ev.pointerId !== g.id) return;
      g.x = ev.clientX;
      g.y = ev.clientY;
      const dist = Math.hypot(ev.clientX - g.x0, ev.clientY - g.y0);
      if (!g.active) {
        if (touch) { if (dist > 8) { end(); setDrag(null); } return; } // a swipe: let the page scroll
        if (dist < 4) return;
        g.active = true;
      }
      autoScroll(ev.clientY);
      preview(g, ev.clientX, ev.clientY);
    };
    g.onUp = (ev) => {
      if (ev.pointerId !== g.id) return;
      end();
      const n = g.last;
      const changed = !!n && (mode === 'create' || n.day !== g.seg.day || n.s !== g.seg.s || n.e !== g.seg.e);
      if (g.active && changed) {
        suppressClick.current = true;
        setTimeout(() => { suppressClick.current = false; }, 0);
        commit(g);
      } else if (g.active) {
        setDrag(null); // held or grabbed without moving: the click that follows opens the event
      } else if (mode === 'create') {
        // A click (or tap) on empty time: a one-hour draft starting at the half hour clicked.
        const s = clamp(Math.floor(origin.min / 30) * 30, 0, DAY_MIN - 60);
        onDraft({ day: origin.day, s, e: s + 60, allDay: false });
      }
    };
    g.onCancel = () => { end(); setDrag(null); };
    g.onTouchMove = (ev) => { if (g.active && ev.cancelable) ev.preventDefault(); };
    g.onKey = (ev) => { if (ev.key === 'Escape' && g.active) { ev.preventDefault(); ev.stopPropagation(); g.onCancel(); } };
    gesture.current = g;
    window.addEventListener('pointermove', g.onMove);
    window.addEventListener('pointerup', g.onUp);
    window.addEventListener('pointercancel', g.onCancel);
    window.addEventListener('keydown', g.onKey, true);
    if (touch) window.addEventListener('touchmove', g.onTouchMove, { passive: false });
    if (touch && mode === 'move') g.timer = setTimeout(activate, 350);
    if (touch && mode === 'resize') activate();
  };

  const onColumnDown = (e) => {
    if (e.target instanceof Element && e.target.closest('.cev, .cal-more')) return;
    begin(e, 'create');
  };
  const openBlock = (item, el, day) => {
    if (suppressClick.current) return;
    onOpen(item, el, day);
  };
  const blockKeys = (item, day) => (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onOpen(item, e.currentTarget, day);
  };

  const renderBlock = (seg) => {
    const { item } = seg;
    const h = pxOf(Math.max(seg.e - seg.s, MIN_BLOCK)) - 2;
    const gut = seg.gutter ? MORE_GUTTER : 0;
    const draggable = canDrag(item);
    return (
      <CalendarEvent
        key={item.id}
        data-id={item.id}
        item={item}
        variant="block"
        height={h}
        today={today}
        clientName={clientName(item)}
        resizable={draggable}
        selected={item.id === selectedId}
        past={seg.day < today || (seg.day === today && seg.e <= nowMin)}
        class={cx(drag && drag.id === item.id && 'is-origin', draggable && 'is-draggable')}
        style={{
          top: `${pxOf(seg.s) + 1}px`,
          height: `${h}px`,
          left: `calc((100% - ${gut}px) * ${seg.col / seg.cols})`,
          width: `calc((100% - ${gut}px) * ${seg.span / seg.cols} - 4px)`,
        }}
        onPointerDown={draggable ? (e) => begin(e, e.target.closest?.('[data-resize]') ? 'resize' : 'move', seg) : undefined}
        onClick={(e) => openBlock(item, e.currentTarget, seg.day)}
        onKeyDown={blockKeys(item, seg.day)}
        onContextMenu={(e) => { if (gesture.current?.touch) e.preventDefault(); }}
      />
    );
  };

  // A dashed preview block: the drag target or the quick-create draft.
  const ghost = (key, { s, e }, title, tone, ref) => {
    const h = pxOf(Math.max(e - s, SNAP)) - 2;
    return (
      <div key={key} ref={ref} class={cx('cev cev-block is-ghost', `tone-${tone}`, h < 30 ? 'cev-l1' : 'cev-l2')} style={{ top: `${pxOf(s) + 1}px`, height: `${h}px`, left: 0, width: 'calc(100% - 4px)' }} aria-hidden="true">
        <span class="cev-line"><span class="cev-title">{title}</span></span>
        {h >= 30 && <span class="cev-meta">{minuteRange(s, e)}</span>}
      </div>
    );
  };

  const adRef = useRef(null);
  const dd = useDayDrag((x) => {
    const r = adRef.current?.getBoundingClientRect();
    if (!r) return null;
    return days[clamp(Math.floor(((x - r.left) * days.length) / r.width), 0, days.length - 1)];
  });
  const laneCap = single ? ALLDAY_LANES + 1 : ALLDAY_LANES; // one day column: room for one more lane
  const collapsed = !expanded && lanes.laneCount > laneCap;
  const shownLanes = collapsed ? laneCap - 1 : lanes.laneCount;
  const hiddenPerDay = days.map((_, i) => (collapsed ? lanes.entries.filter((en) => en.lane >= shownLanes && en.a <= i && en.b >= i).length : 0));
  const draftAllDay = draft && draft.allDay && days.includes(draft.day);
  const rows = Math.max(1, shownLanes + (collapsed ? 1 : 0) + (draftAllDay ? 1 : 0));
  const draftTone = draft ? CALENDAR_TONES[draft.calendarType] || 'info' : 'info';
  const draftTitle = draft?.title?.trim() || '(No title)';

  return (
    <div class={cx('cal-tg', single && 'is-single')} style={{ '--days': days.length }}>
      <div class="cal-tg-head">
        <div class="cal-gutter" />
        {days.map((d) => {
          const label = (
            <>
              <span class="cal-dh-wd">{WEEKDAY_SHORT[weekdayIndex(d)]}</span>
              <span class={cx('cal-dh-num', d === today && 'is-today')}>{dayNumber(d)}</span>
            </>
          );
          return single
            ? <div key={d} class="cal-dh" aria-current={d === today ? 'date' : undefined}>{label}</div>
            : <button key={d} type="button" class="cal-dh" aria-label={`${formatDate(d, 'full')} — open day`} aria-current={d === today ? 'date' : undefined} onClick={() => onPickDay(d)}>{label}</button>;
        })}
      </div>

      <div class="cal-tg-allday">
        <div class="cal-gutter cal-ad-gutter">
          {lanes.laneCount > laneCap && (
            <IconButton icon={expanded ? 'chevronUp' : 'chevronDown'} size="sm" label={expanded ? 'Show fewer all-day items' : 'Show all all-day items'} onClick={() => setExpanded(!expanded)} />
          )}
        </div>
        <div class="cal-ad-grid" ref={adRef} style={{ '--rows': rows }}>
          {days.map((d, i) => (
            <div key={d} class={cx('cal-ad-cell', d === today && 'is-today', dd.drop?.day === d && 'is-drop')} style={{ gridColumn: i + 1 }} onClick={() => onDraft({ day: d, allDay: true })} />
          ))}
          {lanes.entries.filter((en) => en.lane < shownLanes).map((en) => (
            <div key={en.item.id} class="cal-ad-slot" style={{ gridColumn: `${en.a + 1} / ${en.b + 2}`, gridRow: en.lane + 1 }}>
              <CalendarEvent
                item={en.item} data-id={en.item.id} variant="chip" today={today} clientName={clientName(en.item)} selected={en.item.id === selectedId}
                class={cx(canDayDrag(en.item) && 'is-draggable', dd.drop?.id === en.item.id && 'is-origin')}
                onPointerDown={(e) => dd.begin(e, en.item)}
                onClick={(e) => { if (!dd.suppress.current) onOpen(en.item, e.currentTarget, days[en.a]); }}
              />
            </div>
          ))}
          {hiddenPerDay.map((n, i) => n > 0 && (
            <button key={`more-${i}`} type="button" class="cal-ad-more" style={{ gridColumn: i + 1, gridRow: shownLanes + 1 }} onClick={() => setExpanded(true)}>
              +{n} more
            </button>
          ))}
          {draftAllDay && (
            <div class="cal-ad-slot" style={{ gridColumn: days.indexOf(draft.day) + 1, gridRow: rows }}>
              <div ref={draftRef} class={cx('cev cev-chip is-ghost', `tone-${draftTone}`)} aria-hidden="true"><span class="cev-title">{draftTitle}</span></div>
            </div>
          )}
        </div>
      </div>

      <div class="cal-tg-scroll" ref={scrollRef}>
        <div class="cal-tg-body" style={{ height: `${pxOf(DAY_MIN)}px` }}>
          <div class="cal-hours" aria-hidden="true">
            {Array.from({ length: 24 }, (_, h) => <span key={h} class="cal-hour" style={{ top: `${h * HOUR_PX}px` }}>{hourLabel(h)}</span>)}
          </div>
          <div class="cal-cols" ref={colsRef}>
            {columns.map(({ day, placed, more }) => (
              <div key={day} class={cx('cal-col', day === today && 'is-today')} onPointerDown={onColumnDown}>
                {placed.map(renderBlock)}
                {more.map((m) => (
                  <button
                    key={`more-${m.s}`} type="button" class="cal-more" style={{ top: `${pxOf(m.s) + 1}px` }}
                    aria-label={`${m.count} more at ${compactTime(hhmm(m.s))}`}
                    onClick={(e) => onMore(m.items, e.currentTarget, day)}
                  >
                    +{m.count}
                  </button>
                ))}
                {drag && drag.day === day && ghost('drag', drag, drag.mode === 'create' ? draftTitle : shortTitle(dragItem || { title: '' }), drag.mode === 'create' ? draftTone : CALENDAR_TONES[dragItem?.calendarType] || 'info')}
                {draft && !draft.allDay && draft.day === day && !drag && ghost('draft', draft, draftTitle, draftTone, draftRef)}
                {day === today && <div class="cal-now" style={{ top: `${pxOf(nowMin)}px` }} aria-hidden="true" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- day drag (month cells, all-day row): move an event to another day, keeping its times ----------

const canDayDrag = (item) => !isDerived(item) && (item.allDay || !item.end || datePart(item.end) === datePart(item.start));

function moveToDay(item, fromDay, toDay) {
  const diff = diffDays(fromDay, toDay);
  const shift = (v) => (v ? `${addDays(datePart(v), diff)}${v.slice(10)}` : '');
  return dispatch('event.update', { id: item.eventId, patch: { start: shift(item.start), end: shift(item.end) } }).catch(() => {});
}

// dayAt(x, y) → 'YYYY-MM-DD' | null for the grid using it. Mouse starts after 4px; touch after a 350ms hold.
function useDayDrag(dayAt) {
  const [drop, setDrop] = useState(null); // { id, day } while dragging
  const suppress = useRef(false);
  const live = useRef(null);
  useEffect(() => () => live.current?.(), []);
  const begin = (e, item) => {
    if (!canDayDrag(item) || (e.pointerType === 'mouse' && e.button !== 0)) return;
    live.current?.();
    const touch = e.pointerType === 'touch';
    const from = dayAt(e.clientX, e.clientY);
    if (!from) return;
    const g = { id: e.pointerId, x0: e.clientX, y0: e.clientY, active: false, day: from, timer: 0 };
    const stop = () => {
      clearTimeout(g.timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('touchmove', onTouchMove);
      live.current = null;
    };
    const onMove = (ev) => {
      if (ev.pointerId !== g.id) return;
      if (!g.active) {
        const dist = Math.hypot(ev.clientX - g.x0, ev.clientY - g.y0);
        if (touch) { if (dist > 8) stop(); return; }
        if (dist < 4) return;
        g.active = true;
      }
      g.day = dayAt(ev.clientX, ev.clientY) || g.day;
      setDrop({ id: item.id, day: g.day });
    };
    const onUp = (ev) => {
      if (ev.pointerId !== g.id) return;
      stop();
      setDrop(null);
      if (!g.active || g.day === from) return;
      suppress.current = true;
      setTimeout(() => { suppress.current = false; }, 0);
      moveToDay(item, from, g.day);
    };
    const onCancel = () => { stop(); setDrop(null); };
    const onTouchMove = (ev) => { if (g.active && ev.cancelable) ev.preventDefault(); };
    live.current = stop;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    if (touch) {
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      g.timer = setTimeout(() => { g.active = true; setDrop({ id: item.id, day: from }); }, 350);
    }
  };
  return { drop, begin, suppress };
}

// ---------- month ----------

function monthWeeks(date) {
  const start = startOfWeek(startOfMonth(date));
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i)));
}

function MonthGrid({ date, today, index, byDay, onOpen, onDraft, onPickDay, draftRef, draft, selectedId }) {
  const weeks = monthWeeks(date);
  const monthKey = date.slice(0, 7);
  const clientName = (item) => index.clientsById.get(item.clientId)?.name || '';
  const draftTone = draft ? CALENDAR_TONES[draft.calendarType] || 'info' : 'info';
  const dd = useDayDrag((x, y) => document.elementFromPoint(x, y)?.closest?.('.cal-mcell')?.getAttribute('data-day') || null);
  return (
    <div class={cx('cal-month', dd.drop && 'is-dragging')} role="grid" aria-label={formatMonthYear(date)}>
      <div class="cal-month-head" role="row">
        {WEEKDAY_SHORT.map((w, i) => <div key={w} role="columnheader" class="cal-month-wd"><abbr title={WEEKDAY_FULL[i]}>{w}</abbr></div>)}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} class="cal-month-row" role="row">
          {week.map((d) => {
            const list = byDay.get(d) || [];
            const shown = list.length > MONTH_CHIPS ? list.slice(0, MONTH_CHIPS - 1) : list;
            const hidden = list.length - shown.length;
            const isDraft = draft && draft.allDay && draft.day === d;
            return (
              <div
                key={d}
                role="gridcell"
                data-day={d}
                class={cx('cal-mcell', d.slice(0, 7) !== monthKey && 'is-outside', d === today && 'is-today', dd.drop?.day === d && 'is-drop')}
                onClick={(e) => { if (!e.target.closest('button, .cev')) onDraft({ day: d, allDay: true }); }}
              >
                <button type="button" class="cal-mcell-num" aria-label={`${formatDate(d, 'full')} — open day`} aria-current={d === today ? 'date' : undefined} onClick={() => onPickDay(d)}>
                  {dayNumber(d) === 1 ? `${formatDate(d).slice(0, 3)} 1` : dayNumber(d)}
                </button>
                <div class="cal-mcell-body">
                  {shown.map((item) => (
                    <CalendarEvent
                      key={item.id} data-id={item.id} item={item} variant="chip" showTime today={today} clientName={clientName(item)}
                      class={cx(!item.allDay && 'cev-flat', canDayDrag(item) && 'is-draggable', dd.drop?.id === item.id && 'is-origin')}
                      selected={item.id === selectedId}
                      onPointerDown={(e) => dd.begin(e, item)}
                      onClick={(e) => { if (!dd.suppress.current) onOpen(item, e.currentTarget, d); }}
                    />
                  ))}
                  {hidden > 0 && <button type="button" class="cal-mcell-more" onClick={() => onPickDay(d)}>+{hidden} more</button>}
                  {isDraft && (
                    <div ref={draftRef} class={cx('cev cev-chip is-ghost', `tone-${draftTone}`)} aria-hidden="true">
                      <span class="cev-title">{draft.title?.trim() || '(No title)'}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Narrow screens: a dot grid (tap a day to see its items underneath) — 7 text columns are unreadable there.
function MonthCompact({ date, today, index, byDay, onOpen, onDraft, onPickDay, draftRef }) {
  const weeks = monthWeeks(date);
  const monthKey = date.slice(0, 7);
  const list = byDay.get(date) || [];
  return (
    <div class="cal-mc">
      <div class="cal-mc-grid" role="grid" aria-label={formatMonthYear(date)}>
        <div class="cal-mc-row" role="row">
          {WEEKDAY_SHORT.map((w, i) => <div key={w} role="columnheader" class="cal-mc-wd"><abbr title={WEEKDAY_FULL[i]}>{w[0]}</abbr></div>)}
        </div>
        {weeks.map((week) => (
          <div key={week[0]} class="cal-mc-row" role="row">
            {week.map((d) => {
              const dayItems = byDay.get(d) || [];
              return (
                <div key={d} role="gridcell" aria-selected={d === date ? 'true' : 'false'}>
                  <button
                    type="button"
                    tabIndex={d === date ? 0 : -1}
                    class={cx('cal-mc-day', d.slice(0, 7) !== monthKey && 'is-outside', d === today && 'is-today', d === date && 'is-selected')}
                    aria-label={`${formatDate(d, 'full')}${dayItems.length ? `, ${dayItems.length} ${dayItems.length === 1 ? 'item' : 'items'}` : ''}`}
                    onClick={() => setQuery({ date: d })}
                  >
                    <span class="cal-mc-num">{dayNumber(d)}</span>
                    <span class="cal-mc-dots" aria-hidden="true">
                      {dayItems.slice(0, 3).map((it) => <span key={it.id} class={cx('cal-mc-dot', `tone-${isBatchDue(it) ? (isOverdueItem(it, today) ? 'danger' : 'neutral') : CALENDAR_TONES[it.calendarType]}`)} />)}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <DayList day={date} today={today} index={index} list={list} onOpen={onOpen} onDraft={onDraft} onPickDay={onPickDay} draftRef={draftRef} />
    </div>
  );
}

// One day of the agenda: header (opens the Day view) + compact rows; "+" drafts an event on that day.
function DayList({ day, today, index, list, onOpen, onDraft, onPickDay, draftRef }) {
  const clientName = (item) => index.clientsById.get(item.clientId)?.name || '';
  return (
    <section class={cx('cal-dl', day === today && 'is-today')} aria-label={formatDate(day, 'full')}>
      <div class="cal-dl-head">
        <button type="button" class="cal-dl-day" onClick={() => onPickDay(day)} aria-current={day === today ? 'date' : undefined}>
          <span class="cal-dl-num">{dayNumber(day)}</span>
          <span class="cal-dl-wd">{day === today ? 'Today' : WEEKDAY_FULL[weekdayIndex(day)]}<span class="cal-dl-mon"> · {formatDate(day).slice(0, 3)}</span></span>
        </button>
        <IconButton
          icon="plus" size="sm" label={`Add event on ${formatDate(day, 'weekday')}`}
          onClick={(e) => { draftRef.current = e.currentTarget; onDraft({ day, s: 9 * 60, e: 10 * 60, allDay: false }); }}
        />
      </div>
      {list.length ? (
        <div class="cal-dl-list">
          {list.map((item) => (
            <CalendarEvent key={item.id} item={item} variant="row" today={today} clientName={clientName(item)} onClick={(e) => onOpen(item, e.currentTarget, day)} />
          ))}
        </div>
      ) : (
        <p class="cal-dl-empty">Nothing scheduled.</p>
      )}
    </section>
  );
}

function Agenda({ days, today, index, byDay, onOpen, onDraft, onPickDay, draftRef }) {
  return (
    <div class="cal-agenda">
      {days.map((d) => (
        <DayList key={d} day={d} today={today} index={index} list={byDay.get(d) || []} onOpen={onOpen} onDraft={onDraft} onPickDay={onPickDay} draftRef={draftRef} />
      ))}
    </div>
  );
}

// ---------- quick create ----------

const TIME_SLOTS = Array.from({ length: DAY_MIN / SNAP + 1 }, (_, i) => i * SNAP); // 0, 15, … 1440
const CALENDAR_OPTIONS = CALENDAR_TYPES.map((c) => ({ value: c.key, label: c.label }));

function CalendarPick({ value, onChange }) {
  return (
    <span class={cx('cal-pick', `tone-${CALENDAR_TONES[value] || 'info'}`)}>
      <span class="cal-pick-dot" aria-hidden="true" />
      <CompactDropdown value={value} options={CALENDAR_OPTIONS} onChange={onChange} size="sm" width={200} ariaLabel={`Calendar: ${CALENDAR_NAMES[value]}`} />
    </span>
  );
}

// Title · date + time range (or All day) · Calendar → Save. "More options" opens the full event form with what was typed.
// Esc discards; a stray outside click keeps a typed title (DESIGN §4); clicking another empty slot moves the draft.
function QuickCreate({ draft, anchorRef, onChange, onClose, onMore }) {
  const { index, currentPerson } = useStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = useRef(false);
  dirty.current = !!draft.title.trim();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    // Window capture runs before the popover's own Esc handler, so Esc always discards the draft here.
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.popover [role="menu"]')) return; // a calendar menu is open: let it close first
      e.preventDefault();
      e.stopPropagation();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const values = () => ({
    title: draft.title.trim(),
    allDay: !!draft.allDay,
    calendarType: draft.calendarType,
    start: draft.allDay ? draft.day : `${draft.day}T${hhmm(draft.s)}`,
    end: draft.allDay ? '' : `${draft.day}T${hhmm(draft.e)}`,
  });
  const save = (e) => {
    e.preventDefault();
    const v = values();
    if (!v.title) { setError('Add a title.'); e.currentTarget.querySelector('.cal-qc-title')?.focus(); return; }
    if (!v.allDay && draft.e <= draft.s) { setError('End must be after the start.'); return; }
    setBusy(true);
    // Owner = the person on this device (Main Calendar); no kind → a general event.
    const owner = v.calendarType === 'main' && currentPerson && index?.teamById.has(currentPerson) ? currentPerson : '';
    dispatch('event.create', owner ? { ...v, owner } : v)
      .then(() => { toast('Event created.', { tone: 'success' }); onClose(); })
      .catch(() => setBusy(false));
  };
  const setTime = (key, min) => {
    if (!Number.isFinite(min)) return;
    setError('');
    if (key === 's') {
      const dur = Math.max(SNAP, (draft.e ?? min + 60) - (draft.s ?? min));
      onChange({ s: min, e: Math.min(DAY_MIN, min + dur) });
    } else onChange({ e: min });
  };
  const s = draft.s ?? 9 * 60;
  const eMin = draft.e ?? s + 60;

  return (
    <Popover
      open
      onClose={() => { if (!dirty.current) onClose(); }}
      anchorRef={anchorRef}
      placement="bottom-start"
      width={320}
      label="New event"
      class="cal-qc"
    >
      <form class="cal-qc-form" onSubmit={save} noValidate>
        <input
          class="input cal-qc-title" data-autofocus value={draft.title} maxLength={200} placeholder="Add title" aria-label="Title"
          aria-invalid={error === 'Add a title.' ? 'true' : undefined}
          onInput={(e) => { setError(''); onChange({ title: e.currentTarget.value }); }}
        />
        <div class="cal-qc-when">
          <span class="cal-qc-date">{formatDate(draft.day, 'weekday')}</span>
          {!draft.allDay && (
            <span class="cal-qc-times">
              <select class="select input-sm" value={s} aria-label="Start time" onChange={(e) => setTime('s', Number(e.currentTarget.value))}>
                {TIME_SLOTS.filter((m) => m < DAY_MIN).map((m) => <option key={m} value={m}>{compactTime(hhmm(m))}</option>)}
              </select>
              <span aria-hidden="true">–</span>
              <select class="select input-sm" value={eMin} aria-label="End time" onChange={(e) => setTime('e', Number(e.currentTarget.value))}>
                {TIME_SLOTS.filter((m) => m > s).map((m) => <option key={m} value={m}>{m >= DAY_MIN ? '12am' : compactTime(hhmm(m))}</option>)}
              </select>
            </span>
          )}
          <label class="cal-qc-allday">
            <input type="checkbox" checked={!!draft.allDay} onChange={(e) => onChange({ allDay: e.currentTarget.checked, s, e: eMin })} />
            All day
          </label>
        </div>
        <CalendarPick value={draft.calendarType} onChange={(v) => onChange({ calendarType: v })} />
        {error && <p class="field-error" role="alert">{error}</p>}
        <div class="cal-qc-foot">
          <button type="button" class="link cal-qc-more" onClick={() => onMore(values())}>More options</button>
          <span class="spacer" />
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </Popover>
  );
}

// ---------- event popover ----------

// The item behind a popover, re-read from the current state (null once it is gone). Events are found by id, so a
// popover survives the event being moved to another day.
function findItem(state, index, id) {
  if (!state || !index || !id) return null;
  let day = '';
  if (id.startsWith('due-')) day = index.batchesById.get(id.slice(4))?.dueDate || '';
  else day = datePart(index.eventsById.get(id)?.start || '');
  if (!day) return null;
  return withOff(index, calendarItems(state, index, { from: day, to: day })).find((i) => i.id === id) || null;
}

// Batch-due items in an Off month are flagged `off` so they never read as overdue (DESIGN §7.5).
function withOff(index, items) {
  return items.map((i) => (isBatchDue(i) && !('off' in i) ? { ...i, off: isOffBatch(index, { monthId: i.monthId }) } : i));
}

function EventPopover({ target, onClose, afterDelete }) {
  const { state, index, today } = useStore();
  const item = findItem(state, index, target.id);
  const gone = !item;
  useEffect(() => { if (gone) onClose(); }, [gone]);
  if (!item) return null;

  const due = isBatchDue(item);
  const batch = item.batchId ? index.batchesById.get(item.batchId) : null;
  const client = index.clientsById.get(item.clientId);
  const month = item.monthId ? index.monthsById.get(item.monthId) : null;
  const stage = item.stage ? stageByKey(item.stage) : null;
  const owner = teamName(index, item.owner);
  const tone = due ? (isOverdueItem(item, today) ? 'danger' : 'neutral') : CALENDAR_TONES[item.calendarType];
  // Client · Month · Batch — minus what the title already says (a batch-due title names the client and batch number).
  const chain = due
    ? [month ? monthLabel(month.monthNumber) : '', batch?.name]
    : [client?.name, month ? monthLabel(month.monthNumber) : '', batch ? `${batchLabel(batch.batchNumber)} · ${batch.name}` : ''];
  const kindInTitle = !due && item.kind && item.title.startsWith(item.kind);

  const edit = () => {
    onClose();
    openEvent({ event: item });
  };
  const remove = () => {
    dispatch('event.delete', { id: item.eventId })
      .then(() => { toast('Event deleted.'); onClose(); afterDelete?.(); })
      .catch(() => {});
  };
  const setCalendar = (calendarType) => dispatch('event.update', { id: item.eventId, patch: { calendarType } }).catch(() => {});

  return (
    <Popover open onClose={onClose} anchorRef={target.anchorRef} placement="bottom-start" width={352} label={shortTitle(item)} class="cal-ep">
      <div class="cal-ep-head">
        <span class={cx('cal-ep-swatch', `tone-${tone}`)} aria-hidden="true" />
        <div class="cal-ep-heading">
          <h2 class="cal-ep-title">{due ? `${shortTitle(item)} due` : item.title}</h2>
          <p class="cal-ep-when">{whenText(item)}{!due && item.kind && !kindInTitle ? ` · ${item.kind}` : ''}</p>
        </div>
        <IconButton icon="x" size="sm" label="Close" onClick={onClose} />
      </div>

      <div class="cal-ep-body">
        {chain.some(Boolean) && <p class="cal-ep-chain">{chain.filter(Boolean).join(' · ')}</p>}
        {due && batch && (
          <div class="cal-ep-batch">
            <StatusBadge status={batch.status} kind="batch" />
            <StageProgress batch={batch} variant="mini" showLabel />
          </div>
        )}
        {(!due && stage) || owner ? (
          <p class="cal-ep-facts">
            {!due && stage && <span>{stage.label}</span>}
            {owner && <span>{owner}</span>}
            {due && isOverdueItem(item, today) && <span class="text-danger">Overdue</span>}
          </p>
        ) : null}
        {!due && <CalendarPick value={item.calendarType} onChange={setCalendar} />}
        {!due && <NotesField key={item.eventId} eventId={item.eventId} notes={item.notes || ''} />}
      </div>

      <div class="cal-ep-actions">
        {batch && <Button size="sm" variant="primary" href={batchHref(batch.id)} iconRight="arrowUpRight">Open Batch</Button>}
        {!due && <Button size="sm" variant={batch ? 'ghost' : 'secondary'} icon="edit" onClick={edit}>Edit</Button>}
        <span class="spacer" />
        {!due && <ConfirmButton size="sm" variant="ghost" icon="trash" prompt="Delete?" confirmLabel="Delete" onConfirm={remove}>Delete</ConfirmButton>}
      </div>
    </Popover>
  );
}

// Notes edit in place: saved 600ms after typing stops, on blur, and when the popover closes (only `notes` is sent).
function NotesField({ eventId, notes }) {
  const [value, setValue] = useState(notes);
  const saved = useRef(notes);
  const latest = useRef(notes);
  const timer = useRef(0);
  const focused = useRef(false);
  latest.current = value;

  useEffect(() => {
    // Someone else changed the notes: follow them unless this field is being edited.
    if (!focused.current && notes !== saved.current) {
      saved.current = notes;
      setValue(notes);
    }
  }, [notes]);

  const flush = () => {
    clearTimeout(timer.current);
    const next = latest.current;
    if (next === saved.current) return;
    const base = saved.current; // the stored text this edit started from (DATA-03: never overwrite a newer one)
    saved.current = next;
    const send = (text, from) => dispatch('event.update', { id: eventId, patch: { notes: text, notesBase: from } }, { toast: false });
    const failed = (err) => { if (getSnapshot().status === 'ready') toast(err?.message || 'Notes not saved.', { tone: 'danger' }); };
    send(next, base).catch(async (err) => {
      if (!isNotesConflict(err)) return failed(err);
      await Promise.resolve(reload()).catch(() => {});
      const fresh = (getSnapshot().state?.events.find((e) => e.id === eventId)?.notes || '').trim();
      const merged = mergeNotes(base.trim(), next.trim(), fresh).trim();
      saved.current = merged;
      if (!focused.current) setValue(merged);
      return send(merged, fresh).then(() => toast('Someone else edited these notes. Both changes were kept.', { duration: 5000 }), failed);
    });
  };
  useEffect(() => flush, []);

  return (
    <textarea
      class="textarea cal-ep-notes"
      rows={2}
      value={value}
      maxLength={2000}
      placeholder="Add notes"
      aria-label="Notes"
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; flush(); }}
      onInput={(e) => {
        setValue(e.currentTarget.value);
        latest.current = e.currentTarget.value;
        clearTimeout(timer.current);
        timer.current = setTimeout(flush, 600);
      }}
    />
  );
}

// "+N" on an overflowing cluster: every item of that cluster as a compact list.
function MorePopover({ target, today, index, onClose, onOpen }) {
  const list = [...target.items].sort(displaySort);
  const clientName = (item) => index.clientsById.get(item.clientId)?.name || '';
  return (
    <Popover open onClose={onClose} anchorRef={target.anchorRef} placement="bottom-end" width={300} label={`${list.length} events`} class="cal-morepop">
      <p class="cal-morepop-head">{formatDate(target.day, 'weekday')} · {list.length} events</p>
      <div class="cal-morepop-list">
        {list.map((item) => (
          <CalendarEvent
            key={item.id} item={item} variant="row" today={today} clientName={clientName(item)}
            onClick={() => onOpen(item, target.anchorRef.current, target.day)}
          />
        ))}
      </div>
    </Popover>
  );
}
